import { useAtlas } from "@/context/AtlasContext";
import {
  Sparkles, Inbox, Rocket, Layers, BookOpen, Handshake, Users, Flag, FolderKanban, CheckSquare, Boxes, Warehouse, ArrowLeftRight,
  Gauge, Megaphone, Wallet, BarChart3, Bot, Search, Activity, ClipboardList, Store, Package, Tags, ShieldCheck, ScrollText, Plug, Upload,
  UsersRound, Building, LayoutGrid, Contact, LifeBuoy, LayoutDashboard, Target, UserRound, FolderOpen, FileText, GraduationCap, UserPlus, Wrench, Building2, MapPin, Network,
} from "lucide-react";

// Maps a registry module's icon string (modules.py) to a lucide component.
const ICONS = {
  CheckSquare, Target, Users, Bot, Contact, BookOpen, Activity, Boxes, Megaphone, UsersRound,
  FileText, GraduationCap, ScrollText, LifeBuoy, UserPlus, Wrench, Building2, MapPin, Handshake, Package,
};

// Per-module navigation (detailed sub-links only). WHICH modules appear in the rail,
// their order and whether they are enabled all come from the backend registry
// (ctx.installed_modules). This map only supplies each module's links. Keyed by module key.
const MODULE_NAV = {
  tactics: { short: "Tactics", match: ["/tactics", "/work/all", "/work/boards"], sections: [{ items: [
    { to: "/tactics/my-work", label: "My work", icon: CheckSquare, perm: "work.view" },
    { to: "/tactics/initiatives", label: "Missions", icon: Flag, perm: "tactics.projects.view" },
    { to: "/work/all", label: "All work", icon: ClipboardList, perm: "work.view" },
    { to: "/work/boards", label: "Boards", icon: FolderKanban, perm: "work.view" } ] }] },
  people: { short: "Team", match: ["/people-team", "/team-sheet"], sections: [{ items: [
    { to: "/people-team", label: "Directory", icon: Users, end: true },
    { to: "/people-team/org-chart", label: "Org chart", icon: Network, end: true },
    { to: "/team-sheet", label: "Executive Dashboard", icon: LayoutDashboard, perm: "team_sheet.access", end: true },
    { to: "/team-sheet/board", label: "Coaches Board", icon: LayoutGrid, perm: "team_sheet.access" },
    { to: "/team-sheet/drafts", label: "Proposed Drafts", icon: ClipboardList, perm: "team_sheet.access" },
    { to: "/team-sheet/recruiting", label: "Recruiting", icon: UserRound, perm: "team_sheet.access" },
    { to: "/team-sheet/upload", label: "Upload Centre", icon: Upload, perm: "team_sheet.import" } ] }] },
  employee_zero: { short: "Agents", match: ["/employee-zero"], sections: [{ items: [
    { to: "/employee-zero/my-agents", label: "My Agents", icon: Bot },
    { to: "/employee-zero", label: "Find Agents", icon: Search, end: true },
    { to: "/employee-zero/activity", label: "Activity", icon: Activity },
    { to: "/employee-zero/requests", label: "Hiring Requests", icon: ClipboardList } ] }] },
  crm: { short: "CRM", match: ["/crm", "/clients", "/files"], sections: [
    { key: "crm", label: "CRM", items: [
      { to: "/crm/opportunities", label: "Opportunities", icon: Handshake, perm: "crm.opportunities.view" },
      { to: "/crm/organizations", label: "Organizations", icon: Building2, perm: "organizations.view" },
      { to: "/crm/people", label: "Contacts", icon: Users, perm: "people.view" } ] },
    { key: "clients", label: "Clients", items: [
      { to: "/clients", label: "Client workspaces", icon: Building, perm: "clients.view" },
      { to: "/files", label: "Files", icon: FolderOpen, perm: "clients.view" },
      { to: "/admin/ingest", label: "Import", icon: Upload, perm: "organizations.manage" } ] } ] },
  documents: { short: "Documents", match: ["/documents"], sections: [{ items: [
    { to: "/documents", label: "Library", icon: FileText, end: true } ] }] },
  knowledge: { short: "Knowledge", match: ["/knowledge"], sections: [{ items: [
    { to: "/knowledge", label: "Knowledge", icon: BookOpen, perm: "knowledge.view", end: true } ] }] },
  pulse: { short: "Pulse", match: ["/pulse", "/reports", "/portfolio"], sections: [{ items: [
    { to: "/pulse", label: "Overview", icon: Activity, perm: "pulse.view", end: true },
    { to: "/portfolio", label: "Entity overview", icon: Layers, perm: "pulse.view", entities: true },
    { to: "/reports", label: "Digests", icon: BarChart3, perm: "reports.view" },
    { to: "/pulse/explorer", label: "Explorer", icon: Search, perm: "pulse.activity.view", feature: "pulse_activity" },
    { to: "/pulse/sources", label: "Source Health", icon: Plug, perm: "pulse.activity.view", feature: "pulse_activity" } ] }] },
  inventory: { short: "Inventory", match: ["/inventory"], sections: [{ items: [
    { to: "/inventory/items", label: "Items", icon: Boxes, perm: "inventory.items.view" },
    { to: "/inventory/locations", label: "Locations", icon: Warehouse, perm: "inventory.items.view" },
    { to: "/inventory/movements", label: "Movements", icon: ArrowLeftRight, perm: "inventory.items.view" } ] }] },
  marketing: { short: "Marketing", match: ["/marketing"], sections: [{ items: [
    { to: "/marketing", label: "Command Center", icon: Gauge, perm: "marketing.campaigns.view", end: true },
    { to: "/marketing/campaigns", label: "Campaigns", icon: Megaphone, perm: "marketing.campaigns.view" },
    { to: "/marketing/budget", label: "Budgets", icon: Wallet, perm: "marketing.campaigns.view" },
    { to: "/marketing/channels", label: "Channels", icon: BarChart3, perm: "marketing.campaigns.view" } ] }] },
};

// Fixed system zones that are not registry modules.
const ATLAS_MODE = { key: "atlas", label: "Home", short: "Home", icon: Sparkles, home: "/atlas", match: ["/home", "/atlas", "/decisions", "/missions", "/work"], sections: [{ items: [
  { to: "/atlas", label: "Ask Atlas", icon: Sparkles },
  { to: "/work", label: "My work", icon: CheckSquare, perm: "work.view" },
  { to: "/decisions", label: "Decisions", icon: Inbox },
  { to: "/missions", label: "Missions", icon: Rocket, perm: "tasks.view" } ] }] };

const MARKET_MODE = { key: "market", label: "Marketplace", short: "Market", icon: Store, home: "/marketplace", match: ["/marketplace"], sections: [{ items: [
  { to: "/marketplace", label: "Browse", icon: Store, end: true },
  { to: "/marketplace/installed", label: "Installed", icon: Package },
  { to: "/marketplace/categories", label: "Categories", icon: Tags } ] }] };

const ADMIN_MODE = { key: "admin", label: "Administration", short: "Admin", icon: ShieldCheck, admin: true, home: "/admin/users", match: ["/admin", "/companies"], sections: [
  { items: [
    { to: "/admin/users", label: "Users & Access", icon: Users, perm: "users.manage" },
    { to: "/companies", label: "Companies", icon: LayoutGrid, superOnly: true },
    { to: "/admin/operations", label: "Operations", icon: Gauge, perm: "admin.access" },
    { to: "/admin/audit", label: "Audit", icon: ScrollText, perm: "audit.view" },
    { to: "/admin/integrations", label: "Integrations", icon: Plug, perm: "integrations.manage" },
    { to: "/admin/automation", label: "Automation & Slack", icon: Activity, perm: "integrations.manage" },
    { to: "/admin/ingest", label: "Ingest Data", icon: Upload, perm: "organizations.manage" } ] },
  { key: "advanced", label: "Advanced", collapsible: true, items: [
    { to: "/admin/roles", label: "Roles", icon: ShieldCheck, perm: "roles.manage" },
    { to: "/admin/teams", label: "Teams", icon: UsersRound, perm: "teams.manage" },
    { to: "/admin/company", label: "Company", icon: Building, perm: "company.manage" },
    { to: "/admin/industries", label: "Industries", icon: Tags, perm: "company.manage" },
    { to: "/admin/applications", label: "Applications", icon: LayoutGrid, perm: "applications.manage" },
    { to: "/admin/people", label: "People", icon: Contact, perm: "people.view" },
    { to: "/admin/ai-activity", label: "Atlas Activity", icon: Bot, perm: "audit.view" },
    { to: "/admin/support", label: "Support Access", icon: LifeBuoy, supportOnly: true } ] } ] };

// Static definition of every possible mode (system zones + core module defs + the Apps
// aggregator), used by modeFor() to resolve a pathname -> mode key and by pageLabel().
// Core modules keep their own rail slots; all addon (is_core false) modules collapse into Apps.
const CORE_MODE_KEYS = ["tactics", "people", "crm", "documents", "knowledge", "pulse", "employee_zero"];
const MODULE_DEFS = Object.entries(MODULE_NAV).filter(([k]) => CORE_MODE_KEYS.includes(k)).map(([key, d]) => ({ key, label: d.short, short: d.short, ...d }));
const APPS_ADDON_KEYS = Object.keys(MODULE_NAV).filter((k) => !CORE_MODE_KEYS.includes(k));
const APPS_MODE = { key: "apps", label: "Apps", short: "Apps", icon: LayoutGrid, home: "/marketplace", match: APPS_ADDON_KEYS.flatMap((k) => MODULE_NAV[k].match || []), sections: [] };
export const MODES = [ATLAS_MODE, ...MODULE_DEFS, APPS_MODE, MARKET_MODE, ADMIN_MODE];

export const modeFor = (pathname) => {
  let best = MODES[0], bestLen = -1;
  for (const m of MODES) for (const p of (m.match || [])) {
    if ((pathname === p || pathname.startsWith(p + "/")) && p.length > bestLen) { best = m; bestLen = p.length; }
  }
  return best;
};
export const isItemActive = (n, pathname) => n.end ? pathname === n.to : pathname === n.to || pathname.startsWith(n.to + "/");

// Default middle-zone (module) rail order. Core modules first in this order, then any
// enabled addons, then Agents (employee_zero) always last in the middle zone.
export const MIDDLE_DEFAULT = ["tactics", "people", "crm", "documents", "knowledge", "pulse"];
export function orderMiddleKeys(keys, saved) {
  const rank = (k) => { const i = MIDDLE_DEFAULT.indexOf(k); if (i !== -1) return i; if (k === "apps") return 9000; if (k === "employee_zero") return 10000; if (k === "market") return 20000; return 1000; };
  const def = [...keys].sort((a, b) => (rank(a) - rank(b)) || (keys.indexOf(a) - keys.indexOf(b)));
  if (!saved || !saved.length) return def;
  const inSaved = saved.filter((k) => keys.includes(k));
  const rest = def.filter((k) => !inSaved.includes(k));
  return [...inSaved, ...rest];
}

export function useModes() {
  const { user, ctx, has, tenants } = useAtlas();
  const portfolioAdmin = (tenants || []).some((t) => t.company || t.owner);
  const installed = ctx?.installed_modules || [];
  const ok = (n) => (n.supportOnly ? user?.is_platform_support : true) && (n.superOnly ? portfolioAdmin : true) && (!n.entities || (ctx?.entities || []).length) && (!n.feature || (ctx?.features || []).includes(n.feature)) && (!n.perm || has(n.perm));
  const build = (def) => {
    const sections = def.sections.map((s) => ({ ...s, items: s.items.filter(ok) })).filter((s) => s.items.length);
    return { ...def, sections, home: sections[0]?.items[0]?.to || def.home };
  };

  // Core modules keep individual rail slots; every enabled addon collapses into one "Apps" slot.
  const buildModule = (mod) => {
    const icon = ICONS[mod.icon] || Package;
    const home = mod.route || "/marketplace/installed";
    const d = MODULE_NAV[mod.key];
    if (!d) return { key: mod.key, label: mod.name, short: mod.name, icon, home, match: [home], sections: [{ items: [{ to: home, label: mod.name, icon }] }] };
    const built = build({ key: mod.key, label: mod.name, short: d.short || mod.name, icon, home, match: d.match, sections: d.sections });
    if (!built.sections.length) { built.sections = [{ items: [{ to: home, label: mod.name, icon }] }]; built.home = home; }
    return built;
  };

  const coreModes = installed.filter((m) => CORE_MODE_KEYS.includes(m.key)).map(buildModule);
  const addonMods = installed.filter((m) => !CORE_MODE_KEYS.includes(m.key));
  // One collapsible group per enabled addon (registry order), filtered by the user's permissions.
  const appsSections = addonMods.map((mod) => {
    const d = MODULE_NAV[mod.key];
    const icon = ICONS[mod.icon] || Package;
    const items = (d ? d.sections.flatMap((s) => s.items) : [{ to: mod.route || "/marketplace/installed", label: mod.name, icon }]).filter(ok);
    return { key: mod.key, label: mod.name, collapsible: true, items };
  }).filter((s) => s.items.length);
  const appsMatch = addonMods.flatMap((m) => (MODULE_NAV[m.key]?.match) || [m.route]).filter(Boolean);
  const appsMode = { key: "apps", label: "Apps", short: "Apps", icon: LayoutGrid, home: appsSections[0]?.items[0]?.to || "/marketplace", match: appsMatch, sections: appsSections };

  const middleModes = [...coreModes, appsMode];
  const orderedKeys = orderMiddleKeys(middleModes.map((m) => m.key), null);
  const orderedMiddle = orderedKeys.map((k) => middleModes.find((m) => m.key === k)).filter(Boolean);
  return [build(ATLAS_MODE), ...orderedMiddle, build(MARKET_MODE), build(ADMIN_MODE)]
    .filter((m) => (m.sections.length || m.key === "apps") && (!m.admin || ctx?.can_admin));
}

// Backwards-compatible flat groups (used by GlobalSearch / other consumers)
export function useNavGroups() {
  return useModes().flatMap((m) => m.sections.map((s) => ({ key: s.key || m.key, label: s.label || m.label, icon: s.icon || m.icon, items: s.items })));
}
